import { supabase } from '../../lib/supabaseClient';
import type { Contact } from '../contacts/contactService';
import { getContactById } from '../contacts/contactService';
import {
  convertEstimateToInvoice,
  getBillingDocument,
  listLineItems,
  type BillingDocument,
} from './billingService';
import {
  convertPolarQuoteToInvoice,
  getPolarInvoicePayments,
  getPolarPdf,
  issuePolarInvoice,
  issuePolarQuote,
  sendPolarDocument,
  type CimeContactParty,
  type PolarClientDeps,
} from './polarClient';
import { polarPdfId, polarSendId } from './polarDisplay';
import type { Json } from '../../types/database.types';
import { syncPolarPayments, type SyncableDoc, type SyncResult } from '../../lib/billing/syncPayments';

async function getAccessToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Non authentifié');
  return session.access_token;
}

export function contactToPolarParty(c: Contact): CimeContactParty {
  return {
    id: c.id,
    first_name: c.first_name,
    last_name: c.last_name,
    company_name: c.company_name,
    email: c.email,
    phone: c.phone,
    address_line1: c.address_line1,
    postal_code: c.postal_code,
    city: c.city,
    polar_contact_id: c.polar_contact_id,
    polar_organisation_id: c.polar_organisation_id,
    siret: c.siret,
  };
}

export async function persistContactPointers(
  contactId: string,
  pointers: { polar_contact_id: string; polar_organisation_id: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('contacts')
    .update({
      polar_contact_id: pointers.polar_contact_id,
      polar_organisation_id: pointers.polar_organisation_id,
    })
    .eq('id', contactId);
  if (error) throw new Error(`Failed to persist Polar contact pointers: ${error.message}`);
}

export async function persistDocumentPointers(
  documentId: string,
  pointers: {
    polar_quote_id?: string;
    polar_invoice_id?: string;
    number?: string;
    issue_date?: string;
  },
): Promise<void> {
  const { error } = await supabase
    .from('billing_documents')
    .update(pointers)
    .eq('id', documentId);
  if (error) throw new Error(`Failed to persist Polar document pointers: ${error.message}`);
}

export function createPolarClientDeps(overrides: Partial<PolarClientDeps> = {}): PolarClientDeps {
  return {
    getAccessToken,
    persistContactPointers,
    persistDocumentPointers,
    ...overrides,
  };
}

/** Une facture exige l'adresse complète du client (rue, code postal, ville). */
export function hasPostalAddress(
  contact: Pick<Contact, 'address_line1' | 'postal_code' | 'city'>,
): boolean {
  return [contact.address_line1, contact.postal_code, contact.city].every((v) => !!v?.trim());
}

/** Jours entre l'émission et l'échéance (0 si pas d'échéance ou échéance passée). */
export function dueDaysFrom(dueDate: string | null, issueDate: string): number {
  if (!dueDate) return 0;
  const days = Math.round((Date.parse(dueDate) - Date.parse(issueDate)) / 86_400_000);
  return Number.isFinite(days) && days > 0 ? days : 0;
}

export async function figerPolarDocument(
  documentId: string,
  title: string,
): Promise<BillingDocument> {
  const doc = await getBillingDocument(documentId);
  if (!doc) throw new Error('Document introuvable');
  if (!doc.client_contact_id) throw new Error('Choisissez un client avant de figer dans Polar.');
  const contact = await getContactById(doc.client_contact_id);
  if (!contact) throw new Error('Client introuvable');
  if (doc.doc_type === 'invoice' && !hasPostalAddress(contact)) {
    // Mention obligatoire d'une facture : Polar refuse sans adresse (422).
    throw new Error(
      `Adresse du client obligatoire sur une facture : complétez la fiche de ${contact.company_name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'ce client'} (rue, code postal, ville).`,
    );
  }
  const lines = await listLineItems(documentId);
  if (lines.length === 0) throw new Error('Ajoutez au moins une ligne avant de figer.');

  const polarLines = lines.map((l) => ({
    description: l.description,
    quantity: Number(l.quantity),
    unit_price: Number(l.unit_price),
    vat_rate: l.vat_rate === null ? null : Number(l.vat_rate),
  }));
  const issueDate = new Date().toISOString().slice(0, 10);

  if (doc.doc_type === 'invoice') {
    // Facture : directement une facture Polar (pas de devis intermédiaire).
    const issued = await issuePolarInvoice(
      {
        documentId,
        polarInvoiceId: doc.polar_invoice_id,
        contact: contactToPolarParty(contact),
        title,
        notes: doc.notes ?? '',
        lines: polarLines,
        vatRate: Number(doc.vat_rate),
        serviceDate: doc.issue_date,
        dueDays: dueDaysFrom(doc.due_date, issueDate),
      },
      createPolarClientDeps(),
    );
    await persistDocumentPointers(documentId, {
      polar_invoice_id: issued.polar_invoice_id,
      number: issued.number,
      issue_date: issueDate,
    });
  } else {
    const issued = await issuePolarQuote(
      {
        documentId,
        polarQuoteId: doc.polar_quote_id,
        contact: contactToPolarParty(contact),
        title,
        notes: doc.notes ?? '',
        lines: polarLines,
        vatRate: Number(doc.vat_rate),
        serviceDate: doc.issue_date,
      },
      createPolarClientDeps(),
    );
    await persistDocumentPointers(documentId, {
      polar_quote_id: issued.polar_quote_id,
      number: issued.number,
      issue_date: issueDate,
    });
  }
  const updated = await getBillingDocument(documentId);
  if (!updated) throw new Error('Document introuvable après Polar');
  return updated;
}

export async function convertPolarDocument(
  documentId: string,
  serviceDate?: string | null,
): Promise<BillingDocument> {
  const doc = await getBillingDocument(documentId);
  if (!doc) throw new Error('Document introuvable');
  if (!doc.polar_quote_id) throw new Error('Figez le devis dans Polar avant de convertir.');

  const polar = await convertPolarQuoteToInvoice(
    {
      documentId,
      polarQuoteId: doc.polar_quote_id,
      polarInvoiceId: doc.polar_invoice_id,
      serviceDate: serviceDate ?? doc.issue_date,
    },
    createPolarClientDeps(),
  );

  const { error: acceptError } = await supabase.rpc(
    'accept_estimate_for_polar' as 'mark_estimate_accepted',
    { p_id: documentId },
  );
  if (acceptError) throw new Error(`Failed to accept estimate: ${acceptError.message}`);

  const invoice = await convertEstimateToInvoice(documentId);
  const issueDate = new Date().toISOString().slice(0, 10);
  await persistDocumentPointers(invoice.id, {
    polar_invoice_id: polar.polar_invoice_id,
    number: polar.number,
    issue_date: issueDate,
  });
  await persistDocumentPointers(documentId, { polar_invoice_id: polar.polar_invoice_id });
  const updated = await getBillingDocument(invoice.id);
  if (!updated) throw new Error('Facture introuvable après conversion');
  return updated;
}

export async function openPolarPdf(doc: BillingDocument): Promise<void> {
  const id = polarPdfId(doc);
  if (id == null) throw new Error('Pas de PDF Polar pour ce document.');
  const pdf = await getPolarPdf(id, createPolarClientDeps());
  const blob = new Blob([pdf.bytes], { type: pdf.contentType });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function sendCimePolarDocument(documentId: string): Promise<BillingDocument> {
  const doc = await getBillingDocument(documentId);
  if (!doc) throw new Error('Document introuvable');
  const polarId = polarSendId(doc);
  if (polarId == null) throw new Error('Figez le document dans Polar avant de l’envoyer.');
  await sendPolarDocument(polarId, createPolarClientDeps());
  const { error } = await supabase.rpc(
    'mark_polar_document_sent' as 'mark_estimate_accepted',
    { p_id: documentId },
  );
  if (error) throw new Error(`Failed to mark Polar send: ${error.message}`);
  const updated = await getBillingDocument(documentId);
  if (!updated) throw new Error('Document introuvable après envoi Polar');
  return updated;
}

/**
 * Recopie dans CIME Plus les règlements Polaris des pièces données (factures numérotées
 * dans Polaris). Lecture Polaris par la passerelle, écriture par sync_billing_payments.
 */
export async function syncBillingPayments(docs: SyncableDoc[]): Promise<SyncResult> {
  const deps = createPolarClientDeps();
  return syncPolarPayments(docs, {
    fetchInvoice: (polarInvoiceId) => getPolarInvoicePayments(polarInvoiceId, deps),
    save: async (documentId, sync) => {
      const { error } = await supabase.rpc('sync_billing_payments', {
        p_doc_id: documentId,
        p_payments: sync.payments as unknown as Json,
        p_paid: sync.paid,
        p_remaining: sync.remaining,
      });
      if (error) throw new Error(`Failed to sync payments: ${error.message}`);
    },
  });
}
